import React, { useState, useEffect, useCallback } from 'react';
import { Truck, Plus, Search, Pencil, Trash2, X, Phone, Mail, Globe, MapPin, User, Star, Link2, Monitor, KeyRound } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');
const isAdminUser = (u) => u?.role === 'admin' || (u?.extra_roles || []).includes('admin');

const EMPTY = { name: '', category: '', phone: '', email: '', website: '', address: '', notes: '', account_manager: '', payment_terms: '', is_active: true };

export default function Providers() {
  const { user } = useAuth();
  const canEdit = isAdminUser(user);
  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const qs = search ? `?search=${encodeURIComponent(search)}` : '';
      const r = await fetch(`${API_URL}/providers${qs}`, { headers });
      if (!r.ok) throw new Error((await r.json()).error || 'Error');
      setList(await r.json());
    } catch (e) { setError(e.message); setList([]); }
    finally { setLoading(false); }
  }, [search]); // eslint-disable-line

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditingId(null); setShowForm(true); };
  const openEdit = (p) => { setForm({ ...EMPTY, ...p }); setEditingId(p.id); setShowForm(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const url = editingId ? `${API_URL}/providers/${editingId}` : `${API_URL}/providers`;
      const method = editingId ? 'PUT' : 'POST';
      const r = await fetch(url, { method, headers, body: JSON.stringify(form) });
      if (!r.ok) throw new Error((await r.json()).error || 'Error');
      setShowForm(false); load();
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar el proveedor "${p.name}"? Se borran también sus contactos.`)) return;
    try {
      const r = await fetch(`${API_URL}/providers/${p.id}`, { method: 'DELETE', headers });
      if (!r.ok) throw new Error((await r.json()).error || 'Error');
      load();
    } catch (e) { alert(e.message); }
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Truck size={22} /><h1 style={{ margin: 0, fontSize: 22 }}>Proveedores</h1>
        </div>
        {canEdit && (
          <button className="btn btn-primary" onClick={openNew} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Plus size={16} /> Nuevo proveedor
          </button>
        )}
      </div>

      <div style={{ position: 'relative', maxWidth: 380, marginBottom: 16 }}>
        <Search size={16} style={{ position: 'absolute', left: 10, top: 10, color: 'var(--ink-3)' }} />
        <input className="form-input" placeholder="Buscar por nombre o rubro…" value={search}
          onChange={e => setSearch(e.target.value)} style={{ paddingLeft: 32 }} />
      </div>

      {error && <div style={{ color: 'var(--red)', marginBottom: 12 }}>{error}</div>}

      {loading ? <div className="spinner" /> : list.length === 0 ? (
        <div style={{ color: 'var(--ink-2)', padding: 40, textAlign: 'center' }}>Sin proveedores cargados.</div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', fontSize: 12, color: 'var(--ink-2)', background: 'var(--bg-subtle)' }}>
                <th style={{ padding: '10px 14px' }}>Proveedor</th>
                <th style={{ padding: '10px 14px' }}>Rubro</th>
                <th style={{ padding: '10px 14px' }}>Contacto</th>
                <th style={{ padding: '10px 14px', textAlign: 'center' }}>Contactos</th>
                <th style={{ padding: '10px 14px', textAlign: 'center' }}>Activos / Lic.</th>
                <th style={{ padding: '10px 14px' }}>Estado</th>
                {canEdit && <th style={{ padding: '10px 14px' }}></th>}
              </tr>
            </thead>
            <tbody>
              {list.map(p => (
                <tr key={p.id} style={{ borderTop: '1px solid #eef0f2', cursor: 'pointer' }} onClick={() => setDetailId(p.id)}>
                  <td style={{ padding: '10px 14px', fontWeight: 500 }}>{p.name}</td>
                  <td style={{ padding: '10px 14px', color: 'var(--ink-2)' }}>{p.category || '—'}</td>
                  <td style={{ padding: '10px 14px', color: 'var(--ink-2)', fontSize: 13 }}>
                    {p.phone || p.email || '—'}
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'center' }}>{p.contact_count}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'center', color: 'var(--ink-2)' }}>{p.asset_count} / {p.license_count}</td>
                  <td style={{ padding: '10px 14px' }}>
                    <span style={{ fontSize: 11, fontWeight: 500, color: p.is_active ? 'var(--green)' : 'var(--ink-2)' }}>
                      {p.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  {canEdit && (
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                      <button className="btn btn-icon" title="Editar" onClick={() => openEdit(p)}><Pencil size={15} /></button>
                      <button className="btn btn-icon" title="Eliminar" onClick={() => remove(p)} style={{ color: 'var(--red)' }}><Trash2 size={15} /></button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <ProviderForm form={form} setForm={setForm} onClose={() => setShowForm(false)} onSubmit={save} saving={saving} editing={!!editingId} />
      )}

      {detailId && (
        <ProviderDetail id={detailId} canEdit={canEdit} headers={headers} onClose={() => setDetailId(null)} onChanged={load} />
      )}
    </div>
  );
}

// ── Formulario de alta/edición ──────────────────────────────────────────────
function ProviderForm({ form, setForm, onClose, onSubmit, saving, editing }) {
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  return (
    <Modal title={editing ? 'Editar proveedor' : 'Nuevo proveedor'} onClose={onClose} wide>
      <form onSubmit={onSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Razón social *"><input className="form-input" value={form.name} onChange={set('name')} required /></Field>
          <Field label="Rubro / Categoría"><input className="form-input" value={form.category || ''} onChange={set('category')} placeholder="Ej. Hardware, ISP, Software" /></Field>
          <Field label="Teléfono"><input className="form-input" value={form.phone || ''} onChange={set('phone')} /></Field>
          <Field label="Email"><input className="form-input" type="email" value={form.email || ''} onChange={set('email')} /></Field>
          <Field label="Sitio web"><input className="form-input" value={form.website || ''} onChange={set('website')} placeholder="https://" /></Field>
          <Field label="Dirección"><input className="form-input" value={form.address || ''} onChange={set('address')} /></Field>
          <Field label="Ejecutivo de cuenta"><input className="form-input" value={form.account_manager || ''} onChange={set('account_manager')} /></Field>
          <Field label="Condiciones de pago"><input className="form-input" value={form.payment_terms || ''} onChange={set('payment_terms')} placeholder="Ej. 30 días, contado" /></Field>
        </div>
        <Field label="Notas"><textarea className="form-input" value={form.notes || ''} onChange={set('notes')} rows={3} /></Field>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '10px 0', cursor: 'pointer' }}>
          <input type="checkbox" checked={form.is_active !== false} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
          Proveedor activo
        </label>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Guardando…' : editing ? 'Guardar' : 'Crear'}</button>
        </div>
      </form>
    </Modal>
  );
}

// ── Detalle: info + contactos + vínculos ────────────────────────────────────
function ProviderDetail({ id, canEdit, headers, onClose, onChanged }) {
  const [p, setP] = useState(null);
  const [err, setErr] = useState('');
  const [newContact, setNewContact] = useState(null); // {name,role,email,phone,is_primary}
  const [linkType, setLinkType] = useState(null); // 'asset' | 'license'
  const [linkQuery, setLinkQuery] = useState('');
  const [linkResults, setLinkResults] = useState([]);

  const load = useCallback(async () => {
    setErr('');
    try {
      const r = await fetch(`${API_URL}/providers/${id}`, { headers });
      if (!r.ok) throw new Error((await r.json()).error || 'Error');
      setP(await r.json());
    } catch (e) { setErr(e.message); }
  }, [id]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const addContact = async () => {
    if (!newContact?.name?.trim()) return;
    const r = await fetch(`${API_URL}/providers/${id}/contacts`, { method: 'POST', headers, body: JSON.stringify(newContact) });
    if (r.ok) { setNewContact(null); load(); } else alert((await r.json()).error);
  };
  const delContact = async (cid) => {
    if (!window.confirm('¿Eliminar contacto?')) return;
    const r = await fetch(`${API_URL}/providers/contacts/${cid}`, { method: 'DELETE', headers });
    if (r.ok) load();
  };

  const searchLink = async (q) => {
    setLinkQuery(q);
    if (q.trim().length < 2) { setLinkResults([]); return; }
    const url = linkType === 'asset' ? `${API_URL}/assets/search?q=${encodeURIComponent(q)}` : `${API_URL}/providers/search/licenses?q=${encodeURIComponent(q)}`;
    const r = await fetch(url, { headers });
    setLinkResults(r.ok ? await r.json() : []);
  };
  const doLink = async (target) => {
    const r = await fetch(`${API_URL}/providers/${id}/link`, { method: 'POST', headers, body: JSON.stringify({ type: linkType, target_id: target.id }) });
    if (r.ok) { setLinkType(null); setLinkQuery(''); setLinkResults([]); load(); } else alert((await r.json()).error);
  };
  const unlink = async (type, target_id) => {
    const r = await fetch(`${API_URL}/providers/${id}/unlink`, { method: 'POST', headers, body: JSON.stringify({ type, target_id }) });
    if (r.ok) load();
  };

  return (
    <Modal title={p?.name || 'Proveedor'} onClose={onClose} wide>
      {err && <div style={{ color: 'var(--red)' }}>{err}</div>}
      {!p ? <div className="spinner" /> : (
        <div>
          {/* Datos */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 20px', marginBottom: 18 }}>
            {p.category && <Info icon={Truck} label="Rubro">{p.category}</Info>}
            {p.phone && <Info icon={Phone} label="Teléfono">{p.phone}</Info>}
            {p.email && <Info icon={Mail} label="Email">{p.email}</Info>}
            {p.website && <Info icon={Globe} label="Sitio">{p.website}</Info>}
            {p.address && <Info icon={MapPin} label="Dirección">{p.address}</Info>}
            {p.account_manager && <Info icon={User} label="Ejecutivo">{p.account_manager}</Info>}
            {p.payment_terms && <Info icon={KeyRound} label="Pago">{p.payment_terms}</Info>}
          </div>
          {p.notes && <div style={{ background: 'var(--bg-subtle)', borderRadius: 8, padding: 12, fontSize: 13, color: 'var(--ink-2)', marginBottom: 18 }}>{p.notes}</div>}

          {/* Contactos */}
          <Section title={`Contactos (${p.contacts.length})`} action={canEdit && !newContact && (
            <button className="btn btn-sm btn-secondary" onClick={() => setNewContact({ name: '', role: '', email: '', phone: '', is_primary: false })}>+ Contacto</button>
          )}>
            {p.contacts.length === 0 && !newContact && <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>Sin contactos.</div>}
            {p.contacts.map(c => (
              <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #f1f2f4' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
                    {c.is_primary && <Star size={13} fill="var(--amber)" color="var(--amber)" />}{c.name}
                    {c.role && <span style={{ fontSize: 12, color: 'var(--ink-2)', fontWeight: 400 }}>· {c.role}</span>}
                  </div>
                  <div style={{ fontSize: 12.5, color: 'var(--ink-2)' }}>{[c.email, c.phone].filter(Boolean).join(' · ') || '—'}</div>
                </div>
                {canEdit && <button className="btn btn-icon" onClick={() => delContact(c.id)} style={{ color: 'var(--red)' }}><Trash2 size={14} /></button>}
              </div>
            ))}
            {newContact && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10, padding: 12, background: 'var(--bg-subtle)', borderRadius: 8 }}>
                <input className="form-input" placeholder="Nombre *" value={newContact.name} onChange={e => setNewContact(v => ({ ...v, name: e.target.value }))} />
                <input className="form-input" placeholder="Cargo" value={newContact.role} onChange={e => setNewContact(v => ({ ...v, role: e.target.value }))} />
                <input className="form-input" placeholder="Email" value={newContact.email} onChange={e => setNewContact(v => ({ ...v, email: e.target.value }))} />
                <input className="form-input" placeholder="Teléfono" value={newContact.phone} onChange={e => setNewContact(v => ({ ...v, phone: e.target.value }))} />
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                  <input type="checkbox" checked={newContact.is_primary} onChange={e => setNewContact(v => ({ ...v, is_primary: e.target.checked }))} /> Principal
                </label>
                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                  <button className="btn btn-sm btn-secondary" onClick={() => setNewContact(null)}>Cancelar</button>
                  <button className="btn btn-sm btn-primary" onClick={addContact}>Agregar</button>
                </div>
              </div>
            )}
          </Section>

          {/* Vínculos */}
          <Section title="Provee" action={canEdit && (
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-sm btn-secondary" onClick={() => { setLinkType('asset'); setLinkQuery(''); setLinkResults([]); }}>+ Activo</button>
              <button className="btn btn-sm btn-secondary" onClick={() => { setLinkType('license'); setLinkQuery(''); setLinkResults([]); }}>+ Licencia</button>
            </div>
          )}>
            {linkType && (
              <div style={{ marginBottom: 12, padding: 12, background: 'var(--bg-subtle)', borderRadius: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <Link2 size={15} />
                  <input className="form-input" autoFocus placeholder={linkType === 'asset' ? 'Buscar equipo…' : 'Buscar licencia…'} value={linkQuery} onChange={e => searchLink(e.target.value)} style={{ flex: 1 }} />
                  <button className="btn btn-icon" onClick={() => setLinkType(null)}><X size={15} /></button>
                </div>
                {linkResults.map(r => (
                  <div key={r.id} onClick={() => doLink(r)} style={{ padding: '7px 8px', cursor: 'pointer', borderRadius: 6, fontSize: 13 }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--accent-dim)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    {linkType === 'asset' ? (r.hostname + (r.user_name ? ` · ${r.user_name}` : '')) : (r.software_name + (r.vendor ? ` · ${r.vendor}` : ''))}
                  </div>
                ))}
              </div>
            )}
            <LinkRow icon={Monitor} label="Activos" items={p.assets} render={a => a.hostname} onRemove={canEdit ? (a) => unlink('asset', a.id) : null} />
            <LinkRow icon={KeyRound} label="Licencias" items={p.licenses} render={l => l.software_name} onRemove={canEdit ? (l) => unlink('license', l.id) : null} />
          </Section>
        </div>
      )}
    </Modal>
  );
}

// ── Helpers de UI ───────────────────────────────────────────────────────────
function Modal({ title, children, onClose, wide }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }} onClick={onClose}>
      <div className="card" style={{ width: wide ? 640 : 460, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', padding: 20 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>{title}</h2>
          <button className="btn btn-icon" onClick={onClose}><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
function Field({ label, children }) {
  return <div className="form-group" style={{ marginBottom: 10 }}><label className="form-label">{label}</label>{children}</div>;
}
function Section({ title, action, children }) {
  return (
    <div style={{ marginTop: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <h3 style={{ margin: 0, fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--ink-2)' }}>{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}
function Info({ icon: Icon, label, children }) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
      <Icon size={15} style={{ color: 'var(--ink-3)', marginTop: 2 }} />
      <div><div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{label}</div><div style={{ fontSize: 13.5 }}>{children}</div></div>
    </div>
  );
}
function LinkRow({ icon: Icon, label, items, render, onRemove }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}><Icon size={13} /> {label} ({items.length})</div>
      {items.length === 0 ? <div style={{ fontSize: 12.5, color: '#c0c4cc', paddingLeft: 19 }}>—</div> : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, paddingLeft: 19 }}>
          {items.map(it => (
            <span key={it.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, background: 'var(--accent-dim)', color: 'var(--accent)', padding: '3px 9px', borderRadius: 16 }}>
              {render(it)}
              {onRemove && <X size={12} style={{ cursor: 'pointer' }} onClick={() => onRemove(it)} />}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
